import { SiteLayout } from '../components/SiteLayout'
import { ArchiveRow } from './BlogIndexPage'
import { getAuthor, authorPhoto, postsByAuthor } from '../lib/authors'

function LinkedinIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.26 2.37 4.26 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.46C23.21 24 24 23.23 24 22.27V1.73C24 .77 23.21 0 22.23 0z" />
    </svg>
  )
}

function GithubIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.55v-2.07c-3.2.7-3.87-1.36-3.87-1.36-.52-1.34-1.28-1.7-1.28-1.7-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.47.11-3.07 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.21-1.49 3.18-1.18 3.18-1.18.62 1.6.23 2.78.12 3.07.74.81 1.18 1.84 1.18 3.1 0 4.42-2.7 5.39-5.27 5.68.42.36.78 1.05.78 2.13v3.16c0 .3.21.66.8.55 4.56-1.52 7.85-5.83 7.85-10.9C23.5 5.65 18.35.5 12 .5z" />
    </svg>
  )
}

/** Per-author bio + post archive, generated for every author with at least
 * one attributed blog post (see content-routes.tsx's authorRoutes()). */
export function AuthorPage({ slug }: { slug: string }) {
  const author = getAuthor(slug)
  const posts = postsByAuthor(slug)

  if (!author) return null

  const photo = author.photo ?? authorPhoto(slug)

  return (
    <SiteLayout>
      <section className="pt-28 pb-12 md:pt-32">
        <div className="container-wide">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            {photo ? (
              <img
                src={photo}
                alt={author.name}
                className="h-20 w-20 shrink-0 rounded-full border border-ink-100 object-cover"
              />
            ) : null}
            <div>
              <h1 className="display text-[30px] leading-[1.05] text-ink-900 md:text-[42px]">
                {author.name}
              </h1>
              {author.role ? (
                <p className="mt-1 text-[15px] text-ink-500">{author.role}</p>
              ) : null}
              {(author.linkedin || author.github) && (
                <div className="mt-3 flex items-center gap-3">
                  {author.linkedin && (
                    <a
                      href={author.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${author.name} on LinkedIn`}
                      className="text-ink-400 transition-colors hover:text-ink-900"
                    >
                      <LinkedinIcon className="h-[18px] w-[18px]" />
                    </a>
                  )}
                  {author.github && (
                    <a
                      href={author.github}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${author.name} on GitHub`}
                      className="text-ink-400 transition-colors hover:text-ink-900"
                    >
                      <GithubIcon className="h-[18px] w-[18px]" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
          {author.bio ? (
            <p className="mt-6 max-w-[640px] text-[16px] leading-[1.65] text-ink-600">
              {author.bio}
            </p>
          ) : null}
          <p className="mt-4 text-[13px] font-medium uppercase tracking-[0.14em] text-ink-400">
            {posts.length} {posts.length === 1 ? 'post' : 'posts'}
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-wide">
          <div className="border-t border-ink-100">
            {posts.map((p) => (
              <ArchiveRow key={p.slug} entry={p} showAuthor={false} />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  )
}
